import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { formatHumanName } from '@medplum/core';
import {
  addRecentlyViewedClient,
  getRecentlyViewedClients,
  type RecentlyViewedClientRef,
} from '../lib/recentlyViewedClientsCookie';
import { useCurrentUserPractitioner } from '../hooks/useCurrentUserPractitioner';

export interface RecentlyViewedClientEntry {
  patientId: string;
  patientReference: `Patient/${string}`;
  displayName: string;
  viewedAt: string;
}

interface RecentlyViewedClientsContextValue {
  recentlyViewed: RecentlyViewedClientEntry[];
  loading: boolean;
  recordRecentlyViewedClient: (patientId: string) => Promise<void>;
}

const RecentlyViewedClientsContext = createContext<RecentlyViewedClientsContextValue | null>(null);

/**
 * Shares "recently viewed clients" state app-wide so a view recorded by the client
 * chart page (writer) is reflected immediately in the navbar (reader) — without
 * needing a page refresh. Source of truth is a browser cookie scoped per practitioner;
 * see docs/agentic/medplum/medplum-object-models.md.
 */
export function RecentlyViewedClientsProvider({ children }: { children: ReactNode }): JSX.Element {
  const medplum = useMedplum();
  const { practitioner, loading: practLoading } = useCurrentUserPractitioner();
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedClientEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const resolveEntries = useCallback(
    async (refs: RecentlyViewedClientRef[]) => {
      setLoading(true);
      const resolved = await Promise.all(
        refs.map(async (ref): Promise<RecentlyViewedClientEntry | undefined> => {
          try {
            const patient = await medplum.readResource('Patient', ref.patientId);
            return {
              patientId: ref.patientId,
              patientReference: `Patient/${ref.patientId}`,
              displayName: formatHumanName(patient.name?.[0]) || 'Unknown',
              viewedAt: ref.viewedAt,
            };
          } catch {
            // Inaccessible or deleted patient — skip rather than crash the navbar.
            return undefined;
          }
        }),
      );
      setRecentlyViewed(resolved.filter((entry): entry is RecentlyViewedClientEntry => entry !== undefined));
      setLoading(false);
    },
    [medplum],
  );

  useEffect(() => {
    if (practLoading) {
      setLoading(true);
      return;
    }
    if (!practitioner?.id) {
      setRecentlyViewed([]);
      setLoading(false);
      return;
    }
    void resolveEntries(getRecentlyViewedClients(practitioner.id));
  }, [practitioner?.id, practLoading, resolveEntries]);

  const recordRecentlyViewedClient = useCallback(
    async (patientId: string): Promise<void> => {
      const practitionerId = practitioner?.id;
      if (!practitionerId) {
        return;
      }
      try {
        const updated = addRecentlyViewedClient(practitionerId, patientId);
        await resolveEntries(updated);
      } catch (err) {
        console.warn('[recentlyViewed] failed to record client view', err);
      }
    },
    [practitioner?.id, resolveEntries],
  );

  const value = useMemo<RecentlyViewedClientsContextValue>(
    () => ({ recentlyViewed, loading, recordRecentlyViewedClient }),
    [recentlyViewed, loading, recordRecentlyViewedClient],
  );

  return <RecentlyViewedClientsContext.Provider value={value}>{children}</RecentlyViewedClientsContext.Provider>;
}

export function useRecentlyViewedClients(): RecentlyViewedClientsContextValue {
  const ctx = useContext(RecentlyViewedClientsContext);
  if (!ctx) {
    throw new Error('useRecentlyViewedClients must be used within RecentlyViewedClientsProvider');
  }
  return ctx;
}
